import { PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import { globalActions } from 'app/containers/Global/slice';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { mapActions } from 'app/containers/Map/slice';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import { RouteConstants } from 'app/routes';
import i18next from 'i18next';
import { delay, put, select, takeLatest } from 'redux-saga/effects';
import { fieldActions as actions } from '.';
import fieldApi from '../fieldApi';
import { selectShapeFileId } from './selectors';
import {
  IConfirmFieldUploadData,
  IDeleteFieldPayload,
  IFieldInformation,
  IGetFieldNotesPayload,
  IGetFieldPropertiesPayload,
  IGetLayerByShapeIdPayload,
  IImportFieldsFromSeasonResponse,
  ISubmitEditFieldDataPayload,
  ISubmitImportFieldsFromSeasonPayload,
  ISubmitNewFieldsPayload,
  ITempFieldResponse,
  IUpdateFieldInformationData,
} from './types';

import 'leaflet-draw';
import * as L from 'leaflet';

import { reorder } from 'app/containers/Map/slice/helpers';
import { selectSelectedCompanyId } from 'app/containers/Company/slice/selectors';
import { selectOnlyMarkedTempPolygons } from 'app/containers/Map/slice/selectors';
import { ITempPolygon } from 'app/containers/Map/slice/types';
import { getAreaWithUnits } from 'utils/helpers';
import { companyActions } from 'app/containers/Company/slice';
import { generatePath } from 'react-router-dom';

function* getLayerByShapeIdSaga(
  action: PayloadAction<IGetLayerByShapeIdPayload>,
) {
  try {
    const { shapeFileId, envId } = action.payload;

    if (!shapeFileId) {
      throw new Error('Missing shape id.');
    }

    const response: ITempFieldResponse = yield fieldApi.getFieldsById(
      envId,
      shapeFileId,
    );

    yield put(mapActions.getTempFieldsFromTempFieldResponse(response));

    const fieldsList = response.features.map(feature => {
      const coordinatesReordered = reorder(feature.geometry.coordinates);

      const polygon = L.polygon(coordinatesReordered);

      const coords = polygon.getLatLngs().flat(3);

      const area = L.GeometryUtil.geodesicArea(coords);

      const readableArea = getAreaWithUnits(area);

      return { id: feature.id, area, readableArea };
    });
    yield put(actions.setTempFieldsList(fieldsList));
    yield put(actions.setShapeFileId(shapeFileId));
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* submitNewFieldsSaga(action: PayloadAction<ISubmitNewFieldsPayload>) {
  try {
    const fieldIdsNamesObj = action.payload;

    const seasonId = yield select(selectSelectedSeasonId);
    const shapeFileId = yield select(selectShapeFileId);
    const envId = yield select(selectSelectedEnvId);
    if (!envId) {
      return;
    }

    const tempPolygons: ITempPolygon[] = yield select(
      selectOnlyMarkedTempPolygons,
    );

    const fieldsWithNamesObj: IFieldInformation = {};
    let isAnyFieldSelected = false;
    for (let [id, name] of Object.entries(fieldIdsNamesObj)) {
      const thePolygon = tempPolygons.find(polygon => polygon.id === id);

      if (thePolygon) {
        isAnyFieldSelected = true;
        const coordinatesReordered = reorder(thePolygon.coordinates);

        fieldsWithNamesObj[name] = {
          geometryInfo: {
            coordinates: coordinatesReordered,
            type: thePolygon.type,
          },
          propertiesJson: thePolygon.propertiesJson,
        };
      }
    }

    if (!isAnyFieldSelected) {
      notification.warn({
        message: i18next?.t(
          'Field.Notification.NoSelectedNewFieldSubmitError',
          'Please select at least 1 field',
        ),
        placement: 'bottomRight',
      });
      return;
    }

    const data: IConfirmFieldUploadData = {
      fieldInformation: fieldsWithNamesObj,
      seasonId,
      shapeFileId,
    };

    yield fieldApi.confirmFieldUpload(envId, data);

    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.field.list, {
          envId,
        }),
      ),
    );

    yield delay(500);

    notification.success({
      message: i18next?.t('Field.Notification.FieldsAdded', 'New fields added'),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  }
}

function* submitEditFieldDataSaga(
  action: PayloadAction<ISubmitEditFieldDataPayload>,
) {
  try {
    yield put(actions.setEditFieldLoading(true));
    const { companyId, fieldId2, fieldName, notes, envId } = action.payload;

    const data: IUpdateFieldInformationData = {
      companyId,
      fieldId: fieldId2,
      fieldName,
    };

    yield fieldApi.updateFieldInformation(envId, data);
    yield fieldApi.updateFieldNote({ envId, fieldId2, notes });
    yield put(actions.getFieldNotes({ envId, fieldId2 }));
    const seasonId = yield select(selectSelectedSeasonId);
    const selectedCompanyId = yield select(selectSelectedCompanyId);

    if (selectedCompanyId && data.companyId !== selectedCompanyId) {
      yield put(companyActions.setSelectedCompanyId(data.companyId));
    } else {
      yield put(
        mapActions.reloadPolygons({
          envId,
          seasonId,
          companyId: selectedCompanyId,
          companyGroupId: null,
        }),
      );
    }
    yield put(actions.toggleEditFieldDataForm(false));
    yield delay(500);

    notification.success({
      message: i18next?.t(
        'Field.Notification.FieldDataUpdated',
        'Field data successfully updated.',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setEditFieldLoading(false));
  }
}

function* deleteFieldSaga(action: PayloadAction<IDeleteFieldPayload>) {
  try {
    yield put(actions.setDeleteFieldLoading(true));

    const { envId, fieldId } = action.payload;

    yield fieldApi.deleteFieldSeasonById(envId, fieldId);

    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.field.list, {
          envId,
        }),
      ),
    );

    yield delay(500);

    notification.success({
      message: i18next?.t(
        'Field.Notification.FieldDeleted',
        'Field successfully deleted.',
      ),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setDeleteFieldLoading(false));
  }
}

function* getFieldNotesSaga(action: PayloadAction<IGetFieldNotesPayload>) {
  try {
    const { envId, fieldId2 } = action.payload;

    const note = yield fieldApi.getFieldNote({ envId, fieldId2 });
    yield put(actions.setFieldNotes(note));
  } catch (err) {
    yield put(globalActions.handleError(err));
    yield put(actions.setFieldNotes(''));
  }
}

function* getFieldPropertiesSaga(
  action: PayloadAction<IGetFieldPropertiesPayload>,
) {
  try {
    const { envId, fieldId2 } = action.payload;

    const properties = yield fieldApi.getFieldProperties({ envId, fieldId2 });
    yield put(actions.setFieldProperties(properties));
  } catch (err) {
    yield put(actions.setFieldProperties({}));
    // drawn fields don't have metadata (properties)
    if (err?.data?.status !== 404) {
      yield put(globalActions.handleError(err));
    }
  }
}

function* submitImportFieldsFromSeasonSaga(
  action: PayloadAction<ISubmitImportFieldsFromSeasonPayload>,
) {
  try {
    yield put(actions.setImportFieldsFromSeasonLoading(true));

    const { envId, companyId, fromSeasonId, seasonId } = action.payload;
    const response: IImportFieldsFromSeasonResponse = yield fieldApi.importFieldsFromSeason(
      {
        envId,
        companyId,
        fromSeasonId,
        seasonId,
      },
    );

    yield put(actions.setImportFieldsFromSeasonLoading(false));
    yield put(
      globalActions.setRedirectTo(
        generatePath(RouteConstants.field.list, {
          envId,
        }),
      ),
    );

    yield delay(500);

    const importedCount = response.uploadedFieldNames.length;
    notification.success({
      message: i18next?.t('Field.Notification.FieldsImportedFromSeason', {
        defaultValue: '{{count}} fields successfully imported.',
        count: importedCount,
      }),
      placement: 'bottomRight',
    });
  } catch (err) {
    yield put(globalActions.handleError(err));
  } finally {
    yield put(actions.setImportFieldsFromSeasonLoading(false));
    yield put(actions.toggleImportFieldsFromSeasonModal(false));
  }
}

export function* fieldSaga() {
  yield takeLatest(
    actions.submitImportFieldsFromSeason.type,
    submitImportFieldsFromSeasonSaga,
  );

  yield takeLatest(actions.getFieldNotes.type, getFieldNotesSaga);
  yield takeLatest(actions.getFieldProperties.type, getFieldPropertiesSaga);

  yield takeLatest(actions.deleteField.type, deleteFieldSaga);

  yield takeLatest(actions.getLayerByShapeId.type, getLayerByShapeIdSaga);
  yield takeLatest(actions.submitNewFields.type, submitNewFieldsSaga);

  yield takeLatest(actions.submitEditFieldData.type, submitEditFieldDataSaga);
}
